import React, { useState } from "react";
import { Link } from "react-router-dom";
import IntroVideo from '../videos/Axis_background.mp4';
import VideoComponent from './VideoComponent.js';
import Axis_Logo from '../images/Axis_image6.png';
import PageSEO from "../components/seo/PageSEO.js";
import { useScrollBehavior } from './useScrollBehavior.js';
import { CODEX_META, CODEX_ZONES } from './codexData.js';
import "../styles/home.css";
import "../styles/codex.css";

function renderContentItem(item, idx) {
  if (typeof item === 'string') {
    return <p key={idx} className="codex-chapter__para">{item}</p>;
  }
  switch (item.type) {
    case 'section-heading':
      return <h4 key={idx} className="codex-chapter__section-heading">{item.value}</h4>;
    case 'creed':
      return (
        <div key={idx} className="codex-chapter__creed">
          {item.value.split('\n\n').map((para, i) => (
            <p key={i}>{para}</p>
          ))}
        </div>
      );
    case 'mantra':
      return <p key={idx} className="codex-chapter__mantra">{item.value}</p>;
    case 'greeting':
      return <p key={idx} className="codex-chapter__greeting">{item.value}</p>;
    case 'text':
      return <p key={idx} className="codex-chapter__para">{item.value}</p>;
    default:
      return null;
  }
}

export default function CodexPage() {
  useScrollBehavior();
  const [activeZone, setActiveZone] = useState(null);

  const visibleZones = activeZone
    ? CODEX_ZONES.filter(z => z.id === activeZone)
    : CODEX_ZONES;

  return (
    <main className="home">
      <PageSEO
        title="The Axis Codex"
        description="The Axis Codex — a complete philosophical handbook for the reverent revolutionary. Life, the Planet, and the Social Contract."
        path="/codex"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Book",
          "name": "The Axis Codex",
          "author": { "@type": "Person", "name": "Temüjin" },
          "url": (process.env.REACT_APP_SITE_URL || "") + "/codex"
        }}
      />

      <div className="home__bg" aria-hidden="true">
        <VideoComponent videoSrc={IntroVideo} />
      </div>

      <section className="home__content">

        {/* ── HERO ── */}
        <div className="module codex-hero__module">
          <div className="home__logo">
            <img src={Axis_Logo} alt="Axis logo" className="axis_logo" />
          </div>
          <span className="about__eyebrow">The Axis</span>
          <h1 className="home__title">{CODEX_META.title}</h1>
          <p className="home__subtitle">{CODEX_META.tagline}</p>
          <p className="home__copy">
            A complete philosophical handbook — thirty-three chapters across three zones.
            The why, the how, and the who of the Axis practitioner.
            Written by {CODEX_META.author}.
          </p>
          <p className="codex-hero__note">
            This is a working document. It will be updated as the philosophy develops.
          </p>
        </div>

        {/* ── ZONE NAVIGATION ── */}
        <div className="module codex-nav__module">
          <div className="codex-nav">
            <button
              className={`codex-nav__btn ${!activeZone ? 'codex-nav__btn--active' : ''}`}
              onClick={() => setActiveZone(null)}
            >
              All Zones
            </button>
            {CODEX_ZONES.map(zone => (
              <button
                key={zone.id}
                className={`codex-nav__btn ${activeZone === zone.id ? 'codex-nav__btn--active' : ''}`}
                onClick={() => setActiveZone(zone.id)}
              >
                Zone {zone.number} — {zone.title}
              </button>
            ))}
          </div>
        </div>

        {/* ── ZONES + CHAPTERS ── */}
        {visibleZones.map(zone => (
          <div key={zone.id} className="module codex-zone__module" id={zone.id}>

            <div className="codex-zone__header">
              <span className="codex-zone__number">Zone {zone.number}</span>
              <h2 className="codex-zone__title">{zone.title}</h2>
              <p className="codex-zone__subtitle">{zone.subtitle}</p>
            </div>

            <div className="codex-zone__chapters">
              {zone.chapters.map(chapter => (
                <article
                  key={chapter.number}
                  className={`codex-chapter ${chapter.isClosing ? 'codex-chapter--closing' : ''}`}
                  id={`chapter-${chapter.number}`}
                >
                  <div className="codex-chapter__header">
                    <span className="codex-chapter__number">{chapter.number}</span>
                    <h3 className="codex-chapter__title">{chapter.title}</h3>
                  </div>

                  {chapter.pullQuote && (
                    <blockquote className="codex-chapter__pullquote">
                      {chapter.pullQuote}
                    </blockquote>
                  )}

                  <div className="codex-chapter__body">
                    {chapter.content.map((item, idx) => renderContentItem(item, idx))}
                  </div>
                </article>
              ))}
            </div>

          </div>
        ))}

        {/* ── CTA ── */}
        <div className="module codex-cta__module">
          <div className="panel panel--closing">
            <div className="panel__title">Begin the Work</div>
            <div className="panel__body">
              <div className="panel__row">
                <div className="panel__label">Read the Book</div>
                <div className="panel__value">
                  The full Axis philosophy in its definitive form — the complete argument, the complete framework.
                </div>
              </div>
              <div className="panel__row">
                <div className="panel__label">Join the Organization</div>
                <div className="panel__value">
                  The Codex is the map. The organization is the territory. Join the people acting on it.
                </div>
              </div>
            </div>
            <div className="panel__cta">
              <Link className="btn btn--primary" to="/join">Join the Axis</Link>
              <Link className="btn btn--ghost" to="/book">Get the Book</Link>
            </div>
          </div>
        </div>

      </section>
    </main>
  );
}